import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Filter, X } from 'lucide-react';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORT_OPTIONS = [
  { value: 'name', label: 'Nom (A-Z)' },
  { value: 'popularity', label: 'Popularité' },
  { value: 'price_asc', label: 'Prix croissant' },
  { value: 'price_desc', label: 'Prix décroissant' },
  { value: 'rating', label: 'Meilleures notes' },
  { value: 'newest', label: 'Nouveautés' },
];

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [onlyInStock, setOnlyInStock] = useState(false);
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'name');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCategories(); }, []);

  useEffect(() => {
    setSelectedCategory(searchParams.get('category') || '');
    setSearchQuery(searchParams.get('search') || '');
    setSortBy(searchParams.get('sort_by') || 'name');
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, selectedBrand, searchQuery, minPrice, maxPrice, sortBy, onlyInStock]);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data);
    } catch (e) { /* ignore */ }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedBrand) params.append('brand', selectedBrand);
      if (searchQuery) params.append('search', searchQuery);
      if (minPrice) params.append('min_price', minPrice);
      if (maxPrice) params.append('max_price', maxPrice);
      if (sortBy) params.append('sort_by', sortBy);
      if (onlyInStock) params.append('in_stock', 'true');
      const { data } = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(data);
      // Extract unique brands for filter
      const uniqueBrands = [...new Set(data.map((p) => p.brand).filter(Boolean))];
      if (brands.length === 0) setBrands(uniqueBrands);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedBrand('');
    setMinPrice('');
    setMaxPrice('');
    setOnlyInStock(false);
    setSortBy('name');
    setSearchParams({});
  };

  const activeFiltersCount = [
    selectedCategory, selectedBrand, minPrice, maxPrice, onlyInStock
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-surface py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'IBM Plex Sans' }}>
              {searchQuery
                ? `Résultats pour "${searchQuery}"`
                : selectedCategory
                ? categories.find((c) => c.id === selectedCategory)?.name || 'Produits'
                : 'Tous les produits'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {loading ? 'Chargement...' : `${products.length} produit(s) trouvé(s)`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden flex items-center gap-2 px-4 py-2 border border-border rounded-sm bg-white relative"
              data-testid="toggle-filters-button"
            >
              <Filter className="w-4 h-4" />
              Filtres
              {activeFiltersCount > 0 && (
                <span className="bg-brand-orange text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-border rounded-sm bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"
              data-testid="sort-select"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Filters Sidebar */}
          <aside
            className={`${
              showFilters ? 'fixed inset-0 z-50 lg:relative lg:inset-auto' : 'hidden lg:block'
            } lg:w-72 flex-shrink-0`}
          >
            <div className={`bg-white border border-border rounded-sm ${
              showFilters ? 'h-full overflow-auto lg:h-auto' : ''
            }`}>
              {showFilters && (
                <div className="lg:hidden flex items-center justify-between p-4 border-b border-border">
                  <h2 className="font-bold">Filtres</h2>
                  <button onClick={() => setShowFilters(false)} data-testid="close-filters-button">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}

              <div className="p-4 space-y-5">
                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="w-full text-sm text-brand-orange hover:underline text-left"
                    data-testid="clear-filters-button"
                  >
                    Réinitialiser tous les filtres ({activeFiltersCount})
                  </button>
                )}

                {/* Categories */}
                <div>
                  <h3 className="font-semibold mb-3 text-sm uppercase text-muted-foreground">Catégories</h3>
                  <div className="space-y-1">
                    <button
                      onClick={() => setSelectedCategory('')}
                      className={`block w-full text-left px-3 py-2 rounded-sm text-sm ${
                        selectedCategory === '' ? 'bg-brand-orange text-white font-medium' : 'hover:bg-surface'
                      }`}
                      data-testid="category-all-button"
                    >
                      Toutes les catégories
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`block w-full text-left px-3 py-2 rounded-sm text-sm ${
                          selectedCategory === cat.id ? 'bg-brand-orange text-white font-medium' : 'hover:bg-surface'
                        }`}
                        data-testid={`category-filter-${cat.id}`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Brand */}
                {brands.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 text-sm uppercase text-muted-foreground">Marque</h3>
                    <select
                      value={selectedBrand}
                      onChange={(e) => setSelectedBrand(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"
                      data-testid="brand-filter"
                    >
                      <option value="">Toutes les marques</option>
                      {brands.map((b) => (<option key={b} value={b}>{b}</option>))}
                    </select>
                  </div>
                )}

                {/* Price */}
                <div>
                  <h3 className="font-semibold mb-3 text-sm uppercase text-muted-foreground">Prix</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"
                      placeholder="Min €"
                      data-testid="min-price-input"
                    />
                    <input
                      type="number"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"
                      placeholder="Max €"
                      data-testid="max-price-input"
                    />
                  </div>
                </div>

                {/* Stock */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={onlyInStock}
                      onChange={(e) => setOnlyInStock(e.target.checked)}
                      className="w-4 h-4 accent-brand-orange"
                      data-testid="in-stock-checkbox"
                    />
                    <span className="text-sm font-medium">En stock uniquement</span>
                  </label>
                </div>
              </div>

              {showFilters && (
                <div className="lg:hidden p-4 border-t border-border">
                  <button
                    onClick={() => setShowFilters(false)}
                    className="w-full btn-primary py-3 rounded-sm font-semibold"
                  >
                    Voir les résultats ({products.length})
                  </button>
                </div>
              )}
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white border border-border rounded-sm p-4 animate-pulse">
                    <div className="aspect-square bg-surface mb-3"></div>
                    <div className="h-4 bg-surface mb-2"></div>
                    <div className="h-3 bg-surface w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="bg-white border border-border rounded-sm p-12 text-center">
                <p className="text-muted-foreground mb-3">Aucun produit ne correspond à vos critères</p>
                <button
                  onClick={clearFilters}
                  className="btn-primary px-4 py-2 rounded-sm font-medium"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
